
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/base/Header';
import Footer from '../../components/base/Footer';
import { useCart } from '../../hooks/useCart';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { items, getSubtotal, clearCart } = useCart();
  
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    state: ''
  });
  
  const [paymentMethod, setPaymentMethod] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmitOrder = async () => {
    if (!customerData.name || !customerData.email || !customerData.phone || !paymentMethod) {
      setError('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const orderItems = items.map(item => ({
        id: item.id,
        title: `${item.brand} - ${item.name}`,
        quantity: item.quantity,
        unit_price: parseFloat(item.price.replace('R$ ', '').replace(',', '.'))
      }));

      const response = await fetch('https://YOUR-SERVER/create-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          customer: {
            name: customerData.name,
            phone: customerData.phone,
            email: customerData.email
          },
          items: orderItems,
          total_amount: getSubtotal(),
          payment_method: paymentMethod,
          source: 'DLUXMEN-WEB'
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store order data for confirmation page
        const orderData = {
          id: Date.now().toString(),
          items: items,
          customer: customerData,
          total: getSubtotal(),
          paymentMethod: paymentMethod,
          paymentLink: data.payment_link,
          createdAt: new Date().toISOString()
        };
        
        localStorage.setItem('dluxmen-last-order', JSON.stringify(orderData));
        clearCart();
        navigate('/pedido-confirmado');
      } else {
        throw new Error('Erro ao processar pagamento');
      }
    } catch (err) {
      setError('Não foi possível processar seu pedido. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTotal = (total: number) => {
    return `R$ ${total.toFixed(2).replace('.', ',')}`;
  };

  if (items.length === 0) {
    navigate('/carrinho');
    return null;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F8F8' }}>
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-8">
              <h1 
                className="text-3xl font-bold mb-2"
                style={{ 
                  fontFamily: 'Cinzel Decorative, serif',
                  color: '#0B0B0B'
                }}
              >
                Finalizar Compra
              </h1>
              <p 
                className="text-sm"
                style={{ 
                  color: '#C0C0C0',
                  fontFamily: 'Montserrat, sans-serif'
                }}
              >
                Preencha seus dados para finalizar o pedido
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Customer Information */}
              <div>
                <div 
                  className="bg-white rounded-lg p-6 shadow-sm border mb-6"
                  style={{ borderColor: '#C0C0C0' }}
                >
                  <h2 
                    className="text-xl font-bold mb-6"
                    style={{ 
                      fontFamily: 'Playfair Display, serif',
                      color: '#0B0B0B'
                    }}
                  >
                    Dados Pessoais
                  </h2>

                  {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  <div className="space-y-4">
                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ 
                          color: '#0B0B0B',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Nome Completo *
                      </label>
                      <input
                        type="text"
                        value={customerData.name}
                        onChange={(e) => setCustomerData({...customerData, name: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        placeholder="Digite seu nome completo"
                      />
                    </div>

                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ 
                          color: '#0B0B0B',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Email *
                      </label>
                      <input
                        type="email"
                        value={customerData.email}
                        onChange={(e) => setCustomerData({...customerData, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        placeholder="seu@email.com"
                      />
                    </div>

                    <div>
                      <label 
                        className="block text-sm font-medium mb-2"
                        style={{ 
                          color: '#0B0B0B',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Telefone *
                      </label>
                      <input
                        type="tel"
                        value={customerData.phone}
                        onChange={(e) => setCustomerData({...customerData, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent"
                        style={{ fontFamily: 'Montserrat, sans-serif' }}
                        placeholder="(11) 99999-9999"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label 
                          className="block text-sm font-medium mb-2"
                          style={{ 
                            color: '#0B0B0B',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          Cidade
                        </label>
                        <input
                          type="text"
                          value={customerData.city}
                          onChange={(e) => setCustomerData({...customerData, city: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          placeholder="São Paulo"
                        />
                      </div>
                      <div>
                        <label 
                          className="block text-sm font-medium mb-2"
                          style={{ 
                            color: '#0B0B0B',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          Estado
                        </label>
                        <input
                          type="text"
                          value={customerData.state}
                          onChange={(e) => setCustomerData({...customerData, state: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-800 focus:border-transparent"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                          placeholder="SP"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div 
                  className="bg-white rounded-lg p-6 shadow-sm border"
                  style={{ borderColor: '#C0C0C0' }}
                >
                  <h2 
                    className="text-xl font-bold mb-6"
                    style={{ 
                      fontFamily: 'Playfair Display, serif',
                      color: '#0B0B0B'
                    }}
                  >
                    Forma de Pagamento
                  </h2>

                  <div className="space-y-3">
                    {[
                      { value: 'pix', label: 'PIX', icon: 'ri-qr-code-line' },
                      { value: 'credit', label: 'Cartão de Crédito', icon: 'ri-bank-card-line' },
                      { value: 'debit', label: 'Cartão de Débito', icon: 'ri-bank-card-2-line' }
                    ].map((method) => (
                      <label 
                        key={method.value} 
                        className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all duration-200 ${
                          paymentMethod === method.value 
                            ? 'border-green-800 bg-green-50' 
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={method.value}
                          checked={paymentMethod === method.value}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="sr-only"
                        />
                        <i className={`${method.icon} text-xl mr-3`} style={{ color: '#0F3F2F' }}></i>
                        <span 
                          className="text-sm font-medium"
                          style={{ 
                            color: '#0B0B0B',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          {method.label}
                        </span>
                        {paymentMethod === method.value && (
                          <i className="ri-check-line text-green-800 ml-auto"></i>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div>
                <div 
                  className="bg-white rounded-lg p-6 shadow-sm border sticky top-24"
                  style={{ borderColor: '#C0C0C0' }}
                >
                  <h2 
                    className="text-xl font-bold mb-6"
                    style={{ 
                      fontFamily: 'Playfair Display, serif',
                      color: '#0B0B0B'
                    }}
                  >
                    Resumo do Pedido
                  </h2>

                  {/* Order Items */}
                  <div className="space-y-4 mb-6">
                    {items.map((item, index) => (
                      <div 
                        key={`${item.id}-${item.size || 'default'}-${index}`}
                        className="flex items-center space-x-4"
                      >
                        <div className="w-16 h-16 flex-shrink-0">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p 
                            className="text-xs font-medium uppercase tracking-wide"
                            style={{ 
                              color: '#C0C0C0',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {item.brand}
                          </p>
                          <p 
                            className="text-sm font-medium"
                            style={{ 
                              color: '#0B0B0B',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {item.name}
                          </p>
                          <p 
                            className="text-xs"
                            style={{ 
                              color: '#C0C0C0',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            Qtd: {item.quantity}
                          </p>
                        </div>
                        <div>
                          <p 
                            className="text-sm font-bold"
                            style={{ 
                              color: '#0F3F2F',
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {item.price}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div 
                    className="border-t pt-4 space-y-2"
                    style={{ borderColor: '#C0C0C0' }}
                  >
                    <div className="flex justify-between">
                      <span 
                        className="text-sm"
                        style={{ 
                          color: '#C0C0C0',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Subtotal
                      </span>
                      <span 
                        className="text-sm font-medium"
                        style={{ 
                          color: '#0B0B0B',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        {formatTotal(getSubtotal())}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span 
                        className="text-sm"
                        style={{ 
                          color: '#C0C0C0',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Frete
                      </span>
                      <span 
                        className="text-sm"
                        style={{ 
                          color: '#C0C0C0',
                          fontFamily: 'Montserrat, sans-serif'
                        }}
                      >
                        Grátis
                      </span>
                    </div>

                    <div 
                      className="border-t pt-2"
                      style={{ borderColor: '#C0C0C0' }}
                    >
                      <div className="flex justify-between">
                        <span 
                          className="text-lg font-bold"
                          style={{ 
                            color: '#0B0B0B',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          Total
                        </span>
                        <span 
                          className="text-lg font-bold"
                          style={{ 
                            color: '#0F3F2F',
                            fontFamily: 'Montserrat, sans-serif'
                          }}
                        >
                          {formatTotal(getSubtotal())}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="mt-6 space-y-3">
                    <button
                      onClick={handleSubmitOrder}
                      disabled={isLoading}
                      className="w-full py-3 px-6 text-white font-semibold rounded-lg transition-all duration-300 hover:bg-green-700 disabled:opacity-50"
                      style={{ 
                        backgroundColor: '#0F3F2F',
                        fontFamily: 'Montserrat, sans-serif'
                      }}
                    >
                      {isLoading ? 'Processando...' : 'Finalizar Pedido'}
                    </button>

                    <button
                      onClick={() => navigate('/carrinho')}
                      className="w-full py-3 px-6 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-all duration-300"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Voltar ao Carrinho
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
